const { MinimogThemeStyles, MinimogThemeScripts } = window;

if (!window.__MINIMOG_QUICKVIEW_INITIALIZED__) {
  window.__MINIMOG_QUICKVIEW_INITIALIZED__ = true;

  class QuickView {
    constructor() {
      this.modal = new MinimogTheme.Modal();
      this.isOpen = false;
      this.isLoading = false;
      this.requestId = 0;
      this.activeTarget = null;
      this.savedScrollY = 0;

      const triggerSelector =
        ".m-product-quickview-button, .m-product-option--more-option, [data-quickview-trigger='true'], [data-quickview-trigger]";

      const getTrigger = (element) => {
        if (!element || typeof element.closest !== "function") return null;
        return element.closest(triggerSelector);
      };

      const handleTriggerAction = (e, target) => {
        const trigger = target || getTrigger(e.target);
        if (!trigger) return;

        // Intercept immediately BEFORE any product-card navigation or parent handler
        e.preventDefault();
        e.stopPropagation();
        if (typeof e.stopImmediatePropagation === "function") {
          e.stopImmediatePropagation();
        }

        // Prevent duplicate requests while this trigger is already loading
        if (this.isLoading && this.activeTarget === trigger) {
          return;
        }

        const productHandle = this.resolveProductHandle(trigger);
        if (!productHandle) return;

        // Clean up any previously loading target
        if (this.activeTarget && this.activeTarget !== trigger) {
          this.toggleLoading(this.activeTarget, false);
        }

        this.activeTarget = trigger;
        this.toggleLoading(trigger, true);
        this.loadProductQuickView(productHandle, trigger);
      };

      // 1. Capture-phase click listener to intercept BEFORE any card navigation or anchor handlers
      document.addEventListener(
        "click",
        (e) => {
          const trigger = getTrigger(e.target);
          if (trigger) {
            handleTriggerAction(e, trigger);
          }
        },
        true // capture phase
      );

      // 2. Keyboard accessibility: support Enter and Space on Quick View triggers
      document.addEventListener(
        "keydown",
        (e) => {
          if (e.key === "Enter" || e.key === " " || e.keyCode === 13 || e.keyCode === 32) {
            const trigger = getTrigger(e.target);
            if (trigger) {
              handleTriggerAction(e, trigger);
            }
          }
        },
        true // capture phase
      );

      // 3. Fallback delegated listener using theme's addEventDelegate
      if (typeof addEventDelegate === "function") {
        addEventDelegate({
          selector: triggerSelector,
          handler: (e, target) => {
            handleTriggerAction(e, target);
          },
        });
      }

      // Subscribe to cart update events to close quick view after successful ATC
      if (window.MinimogEvents && MinimogTheme?.pubSubEvents?.cartUpdate) {
        window.MinimogEvents.subscribe(MinimogTheme.pubSubEvents.cartUpdate, () => {
          if (this.modal && this.isOpen) {
            this.close();
          }
        });
      }

      // Handle ESC key press to close modal
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.isOpen) {
          this.close();
        }
      });
    }

    // Resolve product handle reliably from trigger or surrounding context
    resolveProductHandle(trigger) {
      if (!trigger) return null;
      let handle = trigger.dataset.productHandle;
      if (!handle) {
        const handleEl = trigger.closest("[data-product-handle]");
        if (handleEl) handle = handleEl.dataset.productHandle;
      }
      if (!handle) {
        const card = trigger.closest(".m-product-card");
        if (card) {
          const elWithHandle = card.querySelector("[data-product-handle]");
          if (elWithHandle) handle = elWithHandle.dataset.productHandle;
        }
      }
      if (!handle) {
        const url = trigger.dataset.productUrl || trigger.closest("a")?.getAttribute("href");
        if (url) {
          const match = url.match(/\/products\/([^\/\?#]+)/);
          if (match) handle = match[1];
        }
      }
      return handle ? handle.trim() : null;
    }

    // Build root-safe URL
    getProductUrl(productHandle) {
      const rootUrl = (window.MinimogSettings?.base_url || window.Shopify?.routes?.root || "/").replace(/\/$/, "");
      return `${rootUrl}/products/${productHandle}`;
    }

    // Load product quick view HTML and assets
    loadProductQuickView(productHandle, triggerTarget) {
      const currentRequestId = ++this.requestId;
      this.isLoading = true;

      // Preserve current scroll position before modal opens
      this.savedScrollY =
        window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

      // If modal is currently open with another product, close and reset inner content immediately
      if (this.isOpen) {
        if (this.modal && this.modal.modalContentInner) {
          this.modal.modalContentInner.innerHTML = "";
        }
        this.isOpen = false;
      }

      const productUrl = this.getProductUrl(productHandle);

      this.loadAssets(
        [MinimogThemeStyles.product, MinimogThemeStyles.productInventory].filter(Boolean),
        "quick-view-assets"
      )
        .then(() => fetchSection("product-quickview", { url: productUrl }))
        .then((html) => {
          // Discard result if a newer request was made in the meantime
          if (this.requestId !== currentRequestId) return;

          this.modalContent = html.querySelector(".m-product-quickview");
          if (!this.modalContent) {
            throw new Error("Could not find .m-product-quickview element in fetched HTML");
          }

          const firstModel = html.querySelector("product-model");
          this.mediaGallery = this.modalContent.querySelector("media-gallery");

          const scriptsToLoad = [
            MinimogThemeScripts.productMedia,
            MinimogThemeScripts.variantsPicker,
            MinimogThemeScripts.productInventory,
          ].filter(Boolean);

          return this.loadAssets(scriptsToLoad, "variants-picker").then(() => {
            if (this.requestId !== currentRequestId) return;

            const colorScheme = this.modalContent.dataset.colorScheme || "";

            // Clear any stale content from previous modal instances
            if (this.modal.modalContentInner) {
              this.modal.modalContentInner.innerHTML = "";
            }

            this.modal.appendChild(this.modalContent);
            this.modal.setWidth("960px");

            // Open modal
            this.modal.open();
            this.modal.setSizes(`m-modal--quickview m-gradient ${colorScheme}`);
            if (this.modal.modal) this.modal.modal.classList.add("m-modal--quickview");
            if (this.modal.modalWrapper) this.modal.modalWrapper.classList.add("m-modal--quickview");

            // Restore/preserve scroll position if browser attempted to jump to top
            const currentScrollY =
              window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
            if (Math.abs(currentScrollY - this.savedScrollY) > 5) {
              window.scrollTo({ top: this.savedScrollY, behavior: "instant" });
            }

            // Ensure media gallery slider in quickview starts at the first/featured media
            const resetQuickViewSlider = () => {
              const gallery = this.modalContent?.querySelector("media-gallery");
              if (gallery?.slider) {
                if (typeof gallery.slider.update === "function") gallery.slider.update();
                if (typeof gallery.slider.slideToLoop === "function") {
                  gallery.slider.slideToLoop(0, 0);
                } else if (typeof gallery.slider.slideTo === "function") {
                  gallery.slider.slideTo(0, 0);
                }
              }
            };
            resetQuickViewSlider();
            requestAnimationFrame(resetQuickViewSlider);
            setTimeout(resetQuickViewSlider, 50);
            setTimeout(resetQuickViewSlider, 150);

            this.toggleLoading(triggerTarget, false);
            this.isOpen = true;
            this.isLoading = false;
            this.activeTarget = null;

            if (firstModel && MinimogThemeScripts.productModel) {
              return this.loadAssets(
                [
                  MinimogThemeScripts.productModel,
                  "https://cdn.shopify.com/shopifycloud/model-viewer-ui/assets/v1.0/model-viewer-ui.css",
                ],
                "product-model-assets"
              );
            }
          });
        })
        .then(() => {
          if (this.requestId !== currentRequestId) return;

          document.dispatchEvent(
            new CustomEvent("quick-view:loaded", {
              detail: { productUrl: productHandle },
            })
          );
        })
        .catch((error) => {
          console.error("Error loading product quick view:", error);
          if (this.requestId === currentRequestId) {
            this.toggleLoading(triggerTarget, false);
            this.isLoading = false;
            this.activeTarget = null;
          }
        });
    }

    // Load assets helper function
    loadAssets(assets, key) {
      return new Promise((resolve, reject) => {
        if (!assets || assets.length === 0) {
          resolve();
          return;
        }
        loadAssetsNew(assets, key, resolve, reject);
      });
    }

    // Close the quick view modal
    close(e) {
      const scrollYToRestore = this.savedScrollY;
      if (this.modal) {
        this.modal.close(e);
        if (this.modal.modalContentInner) {
          // Clear inner HTML after closing to prevent memory leaks and stale elements
          setTimeout(() => {
            if (!this.isOpen && this.modal && this.modal.modalContentInner) {
              this.modal.modalContentInner.innerHTML = "";
            }
            // Ensure scroll position is maintained upon modal removal
            if (scrollYToRestore != null) {
              const currentScroll =
                window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
              if (Math.abs(currentScroll - scrollYToRestore) > 5) {
                window.scrollTo({ top: scrollYToRestore, behavior: "instant" });
              }
            }
          }, 250);
        }
      }
      this.isOpen = false;
      if (this.activeTarget) {
        this.toggleLoading(this.activeTarget, false);
        this.activeTarget = null;
      }
    }

    // Toggle loading spinner on a target element
    toggleLoading(target, isLoading) {
      if (target && target.classList) {
        target.classList[isLoading ? "add" : "remove"]("m-spinner-loading");
      }
    }
  }

  // Initialize the QuickView instance
  MinimogTheme.ProductQuickView = new QuickView();
}
